import {useCallback, useMemo, useRef} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {Variable, VariablesRecord} from '../../common/types/variable-engine'

export const VARIABLE_QS = 'variable'

export type SaveVariable = (varName: string, variable: Variable) => void
type SetVariable = (
  name?: string,
  options?: {replace?: boolean; onSave?: SaveVariable}
) => void

export type ActiveVariable = {
  name: string
  onSave?: SaveVariable
  variable: Variable
}

export type UseActiveVariable = [
  ActiveVariable | undefined,
  SetVariable,
  string | undefined
]

const newVariable: Variable = {type: 'string'}

/**
 * This hook is now best used inside the VariableProvider, hence the name useActionVariableInternal
 *
 * Context: adding a new variable inline into a text field via the dropdowns add new variable option.
 * Having this hook exist in context enables the onSaveRef to be shared between different components
 * i.e. a text area passes an on save callback via useVariableDropdown, which the rendered variable form
 * is able to call
 */
export const useActiveVariableInternal = (
  variables?: VariablesRecord,
  providerId?: string
): UseActiveVariable => {
  const [searchParams] = useSearchParams()
  const qsVariable = searchParams.get(VARIABLE_QS)
  const hasVariable = searchParams.has(VARIABLE_QS)
  const onSaveRef = useRef<SaveVariable>()
  const navigate = useNavigate()

  const activeVariable = useMemo<ActiveVariable | undefined>(() => {
    if (hasVariable) {
      return {
        name: qsVariable as string,
        onSave: onSaveRef.current,
        // ^ This can be set via setVariable. The useVariableDropdown hook allows
        // components to pass this callback. The use case here is adding a new variable
        // inline into text. In which case this onSave is called with the newly added variable
        // allowing the text block to insert the variable inline
        variable:
          variables && qsVariable && variables?.[qsVariable]
            ? variables[qsVariable]
            : newVariable
      }
    }
    return undefined
  }, [qsVariable, hasVariable, variables])

  const setVariable = useCallback<SetVariable>(
    (name, options) => {
      // Can't use `searchParams` in here without breaking memorization
      // We don't need the RR version as this isn't reactive, instead its passed to navigate
      const qs = new URLSearchParams(document.location.search)
      if (name === undefined) {
        qs.delete(VARIABLE_QS)
        onSaveRef.current = undefined
      } else {
        qs.set(VARIABLE_QS, name)
        onSaveRef.current = options?.onSave
      }
      navigate(`${document.location.pathname}?${qs}`, {
        replace: options?.replace
      })
    },
    // intentionally excluding navigate due to RR bug, this is safe as we navigate to a full path anyway
    // https://github.com/remix-run/react-router/issues/7634
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // be very careful about adding deps in here that would cause memorization to fail
    // this will cause useEffect loops if setVariable is redefined on each render
  )

  return [activeVariable, setVariable, providerId]
}
